import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import OpenSeadragon from "openseadragon";
import ZoomInIcon from "../../icons/zoom-in-icon-new.svg";
import ZoomOutIcon from "../../icons/zoom-out-icon-new.svg";
import HomeIcon from "../../icons/home-icon-new.svg";
import FullScreenIcon from "../../icons/full-screen.svg";
import viewerSync from "../../utils/viewer-sync";

const ZOOM_STEP = 1.5;

// The API hands back a plain URL string per image. A tiled descriptor
// (.dzi / .xml / .json) can go straight to OpenSeadragon, while anything else is a
// flat image and needs the simple-image tile source. Detecting from the URL keeps
// the response shape down to one { label, url } pair per image.
//
// The backend guarantees a descriptor here: upstream returns a bare IIIF identifier
// and its adapter appends /info.json before we ever see it. OSD does NOT append it
// itself (openseadragon.js:14217 strips it to recover the identifier), so a bare
// identifier reaching this function would fall to the flat-image branch below and
// open a black panel with no error.
export const toTileSource = (url) =>
  /\.(dzi|xml|json)(\?.*)?$/i.test(url) ? url : { type: "image", url };

// Deliberately NOT built on components/osd-viewer.js. That component is ~590 lines
// wired into the Fabric.js annotation overlay, the socket context, live-sync,
// cropping, the scalebar and heatmaps — none of which a block image needs.
const BlockImageOsd = ({ url, mpp, syncKey, onError }) => {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const onErrorRef = useRef(onError);
  const mppRef = useRef(mpp);
  const syncKeyRef = useRef(syncKey);

  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);

  // Kept in refs so an inline onError from the parent cannot retrigger the effect
  // below and tear down a working viewer on every render.
  useEffect(() => {
    onErrorRef.current = onError;
    mppRef.current = mpp;
    syncKeyRef.current = syncKey;
  });

  useEffect(() => {
    if (!containerRef.current || !url) {
      return undefined;
    }

    setLoading(true);
    setFailed(false);

    const viewer = OpenSeadragon({
      element: containerRef.current,
      tileSources: toTileSource(url),
      // Our own buttons instead of OSD's sprite strip, which would need prefixUrl
      // assets copied into the build for no benefit.
      showNavigationControl: false,
      // No bottom-right minimap. The panel is small enough that the overview cost
      // more space than it gave back, and it sat under the close button. The
      // navigator* options are gone with it — they only apply when this is true.
      // showNavigator: false,
      // visibilityRatio: 1.0,
      // constrainDuringPan: true,
      // crossOriginPolicy is left at its default of false on purpose. Setting it to
      // "Anonymous" — as the main slide viewer does — would make CORS response
      // headers mandatory on the Central dashboard for every tile. This panel never
      // reads canvas pixels (no screenshot tool), so a tainted canvas costs nothing.
      showNavigator: true,
      navigatorAutoFade: false,
      navigatorPosition: "TOP_LEFT",
      navigatorHeight: "100px",
      navigatorWidth: "100px",
      navigatorBorderColor: "#000002ff",
      navigatorDisplayRegionColor: "rgba(230, 17, 10, 0.66)",
    });

    // Pair with the main slide viewer once the image is open (the controller needs
    // the image's size). Without a known µm/px it cannot match scale, so it doesn't
    // pair at all.
    viewer.addHandler("open", () => {
      setLoading(false);
      if (mppRef.current) {
        viewerSync.registerSs(viewer, mppRef.current, syncKeyRef.current);
      }
    });

    viewer.addHandler("open-failed", () => {
      setLoading(false);
      setFailed(true);
      if (onErrorRef.current) {
        onErrorRef.current(url);
      }
    });

    viewerRef.current = viewer;

    return () => {
      viewerSync.unregisterSs(viewer);
      viewer.destroy();
      viewerRef.current = null;
    };
  }, [url]);

  // Through the sync controller, so the zoom limit behaves like the mouse wheel:
  // at the limit, zooming further drives the main slide instead.
  const zoomBy = (factor) => {
    if (viewerRef.current) {
      viewerSync.zoomSsBy(viewerRef.current, factor);
    }
  };

  const goHome = () => {
    if (viewerRef.current) {
      viewerRef.current.viewport.goHome();
    }
  };

  const goFullScreen = () => {
    if (viewerRef.current) {
      viewerRef.current.setFullScreen(true);
    }
  };

  return (
    <div className="block-image-osd">
      <div className="block-image-osd-controls">
        <button type="button" title="Zoom in" onClick={() => zoomBy(ZOOM_STEP)}>
          <img src={ZoomInIcon} alt="Zoom in" />
        </button>
        <button
          type="button"
          title="Zoom out"
          onClick={() => zoomBy(1 / ZOOM_STEP)}
        >
          <img src={ZoomOutIcon} alt="Zoom out" />
        </button>
        <button type="button" title="Reset" onClick={goHome}>
          <img src={HomeIcon} alt="Reset" />
        </button>
        <button type="button" title="Full screen" onClick={goFullScreen}>
          <img src={FullScreenIcon} alt="Full screen" />
        </button>
      </div>

      <div className="block-image-osd-canvas" ref={containerRef} />

      {loading && !failed && (
        <div className="block-image-osd-status">Loading image…</div>
      )}
      {failed && (
        <div className="block-image-osd-status">
          This image could not be loaded.
        </div>
      )}
    </div>
  );
};

BlockImageOsd.propTypes = {
  url: PropTypes.string,
  // µm per pixel of this image (utils/viewer-sync-math.js SECTIONSTAR_STATE_MPP)
  mpp: PropTypes.number,
  // image state (sectioning / tape / slide); White and ClaraSight share an alignment
  syncKey: PropTypes.string,
  onError: PropTypes.func,
};

export default BlockImageOsd;
